// src/pages/StarRec/Accounting.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import * as accountingApi from "../../api/accounting/accountingApi";

/* -------------------- SIMPLE MESSAGE BAR -------------------- */
function MessageBar({ message, onClose }) {
  if (!message) return null;
  return (
    <div className="alert alert-info d-flex justify-content-between align-items-center">
      <div>{message}</div>
      <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
        Close
      </button>
    </div>
  );
}

/* -------------------- SIMPLE MODAL (NO BOOTSTRAP JS) -------------------- */
function Modal({ open, title, children, onClose }) {
  if (!open) return null;

  return (
    <div
      onMouseDown={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        zIndex: 9999,
      }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        className="bg-white border rounded p-3"
        style={{
          width: "min(900px, 100%)",
          maxHeight: "90vh",
          overflow: "auto",
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="m-0">{title}</h5>
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            ✕
          </button>
        </div>

        {children}
      </div>
    </div>
  );
}

/* -------------------- HELPERS -------------------- */
function formatINR(value) {
  const n = Number(value || 0);
  if (Number.isNaN(n)) return "Rs.0";
  return "Rs." + n.toLocaleString("en-IN");
}

function toDateInputValue(dateLike) {
  if (!dateLike) return "";
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function safeText(v) {
  return (v ?? "").toString().trim();
}

/* -------------------- FILTER MODAL -------------------- */
function FilterModal({
  open,
  onClose,
  countryOptions,
  stateOptions,
  cityOptions,
  value,
  onChange,
  onApply,
  onReset,
}) {
  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose} title="Location / Filter">
      <div className="row g-3">
        <div className="col-md-6">
          <label className="form-label">Country</label>
          <select
            className="form-select"
            value={value.country}
            onChange={(e) =>
              onChange({
                ...value,
                country: e.target.value,
                state: "",
                city: "",
              })
            }
          >
            <option value="">All</option>
            {countryOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-6">
          <label className="form-label">State</label>
          <select
            className="form-select"
            value={value.state}
            disabled={!value.country}
            onChange={(e) =>
              onChange({
                ...value,
                state: e.target.value,
                city: "",
              })
            }
          >
            <option value="">All</option>
            {stateOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-6">
          <label className="form-label">City</label>
          <select
            className="form-select"
            value={value.city}
            disabled={!value.state}
            onChange={(e) => onChange({ ...value, city: e.target.value })}
          >
            <option value="">All</option>
            {cityOptions.map((ct) => (
              <option key={ct} value={ct}>
                {ct}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-6">
          <label className="form-label">Transaction Type</label>
          <select
            className="form-select"
            value={value.type}
            onChange={(e) => onChange({ ...value, type: e.target.value })}
          >
            <option value="">All</option>
            <option value="income">Income</option>
            <option value="expense">Expense</option>
          </select>
        </div>
      </div>

      <div className="d-flex justify-content-end gap-2 mt-3">
        <button className="btn btn-outline-secondary" onClick={onClose}>
          Back
        </button>
        <button className="btn btn-outline-danger" onClick={onReset}>
          Reset
        </button>
        <button className="btn btn-outline-primary" onClick={onApply}>
          Filter
        </button>
      </div>
    </Modal>
  );
}

/* -------------------- ADD TRANSACTION MODAL -------------------- */
function TransactionModal({
  open,
  onClose,
  mode,
  countryOptions,
  stateOptions,
  cityOptions,
  value,
  onChange,
  onSave,
  saving,
}) {
  if (!open) return null;

  const title = mode === "income" ? "Add Income" : "Add Expense";

  return (
    <Modal open={open} onClose={onClose} title={title}>
      <div className="row g-3">
        <div className="col-md-6">
          <label className="form-label">Country</label>
          <select
            className="form-select"
            value={value.country}
            onChange={(e) =>
              onChange({
                ...value,
                country: e.target.value,
                state: "",
                city: "",
              })
            }
          >
            <option value="">Select</option>
            {countryOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-6">
          <label className="form-label">State</label>
          <select
            className="form-select"
            value={value.state}
            disabled={!value.country}
            onChange={(e) =>
              onChange({
                ...value,
                state: e.target.value,
                city: "",
              })
            }
          >
            <option value="">Select</option>
            {stateOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-6">
          <label className="form-label">City</label>
          <select
            className="form-select"
            value={value.city}
            disabled={!value.state}
            onChange={(e) => onChange({ ...value, city: e.target.value })}
          >
            <option value="">Select</option>
            {cityOptions.map((ct) => (
              <option key={ct} value={ct}>
                {ct}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-6">
          <label className="form-label">Transaction Mode</label>
          <select
            className="form-select"
            value={value.txnMode}
            onChange={(e) => onChange({ ...value, txnMode: e.target.value })}
          >
            <option value="">Select</option>
            <option value="Cash">Cash</option>
            <option value="Online">Online</option>
            <option value="Bank">Bank</option>
            <option value="UPI">UPI</option>
            <option value="gpay">gpay</option>
            <option value="phonepe">phonepe</option>
          </select>
        </div>

        <div className="col-md-6">
          <label className="form-label">Amount</label>
          <input
            className="form-control"
            type="number"
            value={value.amount}
            onChange={(e) => onChange({ ...value, amount: e.target.value })}
            placeholder="Enter amount"
          />
        </div>

        <div className="col-md-6">
          <label className="form-label">Date</label>
          <input
            className="form-control"
            type="date"
            value={value.date}
            onChange={(e) => onChange({ ...value, date: e.target.value })}
          />
        </div>

        <div className="col-12">
          <label className="form-label">Brief / Description</label>
          <input
            className="form-control"
            value={value.brief}
            onChange={(e) => onChange({ ...value, brief: e.target.value })}
            placeholder="Income Brief / Expense Brief"
          />
        </div>
      </div>

      <div className="d-flex justify-content-end gap-2 mt-3">
        <button className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>
          Back
        </button>
        <button className="btn btn-outline-primary" onClick={onSave} disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </Modal>
  );
}

/* -------------------- PAGE -------------------- */
const Accounting = () => {
  // ✅ IMPORTANT: declare ref INSIDE component (this fixes your no-undef)
  const didLoadRef = useRef(false);

  useEffect(() => {
    document.title = "StarRec Accounting";
  }, []);

  const businessHeader = useMemo(
    () => ({
      businessName: "Business Name",
      branchLine: "Branch Name, City, State, Country",
    }),
    []
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [transactions, setTransactions] = useState([]);

  /* -------------------- MESSAGE -------------------- */
  const [message, setMessage] = useState("");
  const showMessage = (txt) => {
    setMessage(txt);
    window.clearTimeout(window.__msgTimer);
    window.__msgTimer = window.setTimeout(() => setMessage(""), 2000);
  };

  /* -------------------- LOAD DATA -------------------- */
  const normalizeTx = (tx) => {
    const id = tx?._id || tx?.id || `T-${Math.random().toString(16).slice(2)}`;
    return {
      id,
      type: safeText(tx?.type) || "income",
      country: safeText(tx?.country),
      state: safeText(tx?.state),
      city: safeText(tx?.city),
      txnMode: safeText(tx?.mode) || safeText(tx?.txnMode),
      amount: Number(tx?.amount || 0),
      date: toDateInputValue(tx?.date),
      brief: safeText(tx?.description) || safeText(tx?.brief) || "",
      raw: tx,
    };
  };

  const loadRevenue = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await accountingApi.getRevenueReport();
      const txs = Array.isArray(res?.transactions) ? res.transactions : [];
      setTransactions(txs.map(normalizeTx));
    } catch (e) {
      setError(e?.message || "Failed to load revenue report");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Prevent double call in React 18 dev
  useEffect(() => {
    if (didLoadRef.current) return;
    didLoadRef.current = true;
    loadRevenue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* -------------------- OPTIONS (derived from data) -------------------- */
  const countryOptions = useMemo(() => {
    const set = new Set();
    transactions.forEach((t) => t.country && set.add(t.country));
    return Array.from(set).sort();
  }, [transactions]);

  /* -------------------- FILTER STATE -------------------- */
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterDraft, setFilterDraft] = useState({
    country: "",
    state: "",
    city: "",
    type: "",
  });
  const [filterApplied, setFilterApplied] = useState({
    country: "",
    state: "",
    city: "",
    type: "",
  });

  const filteredTransactions = useMemo(() => {
    return transactions
      .filter((t) => {
        if (filterApplied.country && t.country !== filterApplied.country) return false;
        if (filterApplied.state && t.state !== filterApplied.state) return false;
        if (filterApplied.city && t.city !== filterApplied.city) return false;
        if (filterApplied.type && t.type !== filterApplied.type) return false;
        return true;
      })
      .sort((a, b) => String(b.date).localeCompare(String(a.date)));
  }, [transactions, filterApplied]);

  /* -------------------- DEPENDENT OPTIONS (NO ESLINT WARNINGS) -------------------- */
  const filterStateOptions = useMemo(() => {
    if (!filterDraft.country) return [];
    const set = new Set();
    transactions.forEach((t) => {
      if (t.state && t.country === filterDraft.country) set.add(t.state);
    });
    return Array.from(set).sort();
  }, [filterDraft.country, transactions]);

  const filterCityOptions = useMemo(() => {
    if (!filterDraft.country || !filterDraft.state) return [];
    const set = new Set();
    transactions.forEach((t) => {
      if (t.city && t.country === filterDraft.country && t.state === filterDraft.state) {
        set.add(t.city);
      }
    });
    return Array.from(set).sort();
  }, [filterDraft.country, filterDraft.state, transactions]);

  /* -------------------- TOTAL REVENUE -------------------- */
  const totalRevenue = useMemo(() => {
    const income = filteredTransactions
      .filter((t) => t.type === "income")
      .reduce((sum, t) => sum + Number(t.amount || 0), 0);

    const expense = filteredTransactions
      .filter((t) => t.type === "expense")
      .reduce((sum, t) => sum + Number(t.amount || 0), 0);

    return income - expense;
  }, [filteredTransactions]);

  const incomeRows = useMemo(
    () => filteredTransactions.filter((t) => t.type === "income"),
    [filteredTransactions]
  );

  const expenseRows = useMemo(
    () => filteredTransactions.filter((t) => t.type === "expense"),
    [filteredTransactions]
  );

  /* -------------------- ADD TXN -------------------- */
  const [addOpen, setAddOpen] = useState(false);
  const [addMode, setAddMode] = useState("income");
  const [saving, setSaving] = useState(false);

  const [txnDraft, setTxnDraft] = useState({
    country: "",
    state: "",
    city: "",
    txnMode: "",
    amount: "",
    date: "",
    brief: "",
  });

  const openAdd = (mode) => {
    setAddMode(mode);
    setTxnDraft({
      country: filterApplied.country || "",
      state: filterApplied.state || "",
      city: filterApplied.city || "",
      txnMode: "",
      amount: "",
      date: "",
      brief: "",
    });
    setAddOpen(true);
  };

  const closeAdd = () => setAddOpen(false);

  const addStateOptions = useMemo(() => {
    if (!txnDraft.country) return [];
    const set = new Set();
    transactions.forEach((t) => {
      if (t.state && t.country === txnDraft.country) set.add(t.state);
    });
    return Array.from(set).sort();
  }, [txnDraft.country, transactions]);

  const addCityOptions = useMemo(() => {
    if (!txnDraft.country || !txnDraft.state) return [];
    const set = new Set();
    transactions.forEach((t) => {
      if (t.city && t.country === txnDraft.country && t.state === txnDraft.state) {
        set.add(t.city);
      }
    });
    return Array.from(set).sort();
  }, [txnDraft.country, txnDraft.state, transactions]);

  const saveTxn = async () => {
    if (!txnDraft.country || !txnDraft.state || !txnDraft.city) {
      alert("Please select Country, State, City");
      return;
    }
    if (!txnDraft.txnMode) {
      alert("Please select Transaction Mode");
      return;
    }
    const amt = Number(txnDraft.amount);
    if (!amt || amt <= 0) {
      alert("Please enter valid Amount");
      return;
    }
    if (!txnDraft.date) {
      alert("Please select Date");
      return;
    }

    const payload = {
      type: addMode,
      mode: txnDraft.txnMode,
      description: txnDraft.brief || (addMode === "income" ? "Income" : "Expense"),
      amount: amt,
      currency: "INR",
      country: txnDraft.country,
      state: txnDraft.state,
      city: txnDraft.city,
      date: txnDraft.date,
    };

    setSaving(true);
    try {
      await accountingApi.addTransaction(payload);
      showMessage(addMode === "income" ? "Income added successfully" : "Expense added successfully");
      setAddOpen(false);
      await loadRevenue();
    } catch (e) {
      alert(e?.message || "Failed to add transaction");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container py-4">
      <h1 className="fs-4 fw-bold mb-3">Accounting</h1>

      <MessageBar message={message} onClose={() => setMessage("")} />

      {error ? (
        <div className="alert alert-danger d-flex justify-content-between align-items-center">
          <div>{error}</div>
          <button className="btn btn-sm btn-outline-danger" onClick={loadRevenue}>
            Retry
          </button>
        </div>
      ) : null}

      <div className="card mb-3">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <div className="fw-bold">Total Revenue</div>
              <div className="text-muted small">From Date till Today</div>
            </div>
            <div className="fs-4 fw-bold">
              {loading ? "Loading..." : formatINR(totalRevenue)}
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex gap-2 mb-3">
        <button className="btn btn-outline-secondary" onClick={() => setFilterOpen(true)}>
          Filters
        </button>
        <button className="btn btn-outline-danger" onClick={() => openAdd("expense")}>
          Add Expense
        </button>
        <button className="btn btn-outline-success" onClick={() => openAdd("income")}>
          Add Income
        </button>

        <button className="btn btn-outline-primary ms-auto" onClick={loadRevenue} disabled={loading}>
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <div className="border rounded p-3 mb-3">
        <div className="fw-semibold">{businessHeader.businessName}</div>
        <div className="text-muted small">{businessHeader.branchLine}</div>
      </div>

      <div className="mb-4">
        <h6 className="fw-bold mb-2">Income</h6>
        <div className="table-responsive">
          <table className="table table-bordered table-striped align-middle">
            <thead>
              <tr>
                <th style={{ width: 120 }}>Date</th>
                <th>Brief</th>
                <th style={{ width: 120 }}>Mode</th>
                <th style={{ width: 140 }} className="text-end">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {incomeRows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center">
                    No income transactions
                  </td>
                </tr>
              ) : (
                incomeRows.map((t) => (
                  <tr key={t.id}>
                    <td>{t.date || "-"}</td>
                    <td>{t.brief || "-"}</td>
                    <td>{t.txnMode || "-"}</td>
                    <td className="text-end">{formatINR(t.amount)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mb-2">
        <h6 className="fw-bold mb-2">Expense</h6>
        <div className="table-responsive">
          <table className="table table-bordered table-striped align-middle">
            <thead>
              <tr>
                <th style={{ width: 120 }}>Date</th>
                <th>Brief</th>
                <th style={{ width: 120 }}>Mode</th>
                <th style={{ width: 140 }} className="text-end">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {expenseRows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center">
                    No expense transactions
                  </td>
                </tr>
              ) : (
                expenseRows.map((t) => (
                  <tr key={t.id}>
                    <td>{t.date || "-"}</td>
                    <td>{t.brief || "-"}</td>
                    <td>{t.txnMode || "-"}</td>
                    <td className="text-end">{formatINR(t.amount)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <FilterModal
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        countryOptions={countryOptions}
        stateOptions={filterDraft.country ? filterStateOptions : []}
        cityOptions={filterDraft.country && filterDraft.state ? filterCityOptions : []}
        value={filterDraft}
        onChange={setFilterDraft}
        onReset={() => {
          const reset = { country: "", state: "", city: "", type: "" };
          setFilterDraft(reset);
          setFilterApplied(reset);
          setFilterOpen(false);
          showMessage("Filters reset");
        }}
        onApply={() => {
          setFilterApplied(filterDraft);
          setFilterOpen(false);
          showMessage("Filter applied");
        }}
      />

      <TransactionModal
        open={addOpen}
        onClose={closeAdd}
        mode={addMode}
        countryOptions={countryOptions.length ? countryOptions : ["India", "UAE", "USA"]}
        stateOptions={txnDraft.country ? addStateOptions : []}
        cityOptions={txnDraft.country && txnDraft.state ? addCityOptions : []}
        value={txnDraft}
        onChange={setTxnDraft}
        onSave={saveTxn}
        saving={saving}
      />
    </div>
  );
};

export default Accounting;
